import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
}

const SEO = ({
  title = 'StaffPlus RH - Gestão Trabalhista e Consultoria Jurídica Especializada',
  description = 'Gestão completa de Recursos Humanos e rotinas trabalhistas para construtoras, empreiteiras e prestadores de serviços. Consultoria jurídica especializada, controle de ponto, folha de pagamento, admissão e desligamento.',
  keywords = 'gestão de RH, recursos humanos, folha de pagamento, admissão, desligamento, controle de ponto, férias, atestados, consultoria trabalhista, legislação trabalhista, esocial, construção civil, empreiteiras, terceirização RH, gestão trabalhista, auditoria trabalhista, conformidade trabalhista',
  canonicalUrl = 'https://staffplusrh.com.br',
  ogImage = 'https://staffplusrh.com.br/og-image.jpg',
  ogType = 'website',
  twitterCard = 'summary_large_image',
}: SEOProps) => {
  useEffect(() => {
    // Update document title
    document.title = title;

    // Helper function to set meta tags
    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Standard meta tags
    setMetaTag('description', description);
    setMetaTag('keywords', keywords);
    setMetaTag('author', 'StaffPlus RH');
    setMetaTag('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    setMetaTag('googlebot', 'index, follow');

    // Open Graph meta tags
    setMetaTag('og:title', title, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:type', ogType, true);
    setMetaTag('og:url', canonicalUrl, true);
    setMetaTag('og:image', ogImage, true);
    setMetaTag('og:image:width', '1200', true);
    setMetaTag('og:image:height', '630', true);
    setMetaTag('og:site_name', 'StaffPlus RH', true);
    setMetaTag('og:locale', 'pt_BR', true);

    // Twitter Card meta tags
    setMetaTag('twitter:card', twitterCard);
    setMetaTag('twitter:title', title);
    setMetaTag('twitter:description', description);
    setMetaTag('twitter:image', ogImage);

    // Additional SEO meta tags
    setMetaTag('theme-color', '#0F3D4C');
    setMetaTag('format-detection', 'telephone=no');

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonicalUrl;

    // Structured Data (JSON-LD)
    let scriptTag = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.type = 'application/ld+json';
      document.head.appendChild(scriptTag);
    }

    const structuredData = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': `${canonicalUrl}/#organization`,
          name: 'StaffPlus RH',
          url: canonicalUrl,
          logo: {
            '@type': 'ImageObject',
            url: `${canonicalUrl}/logo.png`,
          },
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: '+55-47-9292-3530',
            contactType: 'customer service',
            areaServed: 'BR',
            availableLanguage: ['Portuguese'],
          },
          sameAs: [
            'https://www.instagram.com/staffplus_rh/',
          ],
        },
        {
          '@type': 'WebSite',
          '@id': `${canonicalUrl}/#website`,
          url: canonicalUrl,
          name: 'StaffPlus RH',
          description: description,
          publisher: {
            '@id': `${canonicalUrl}/#organization`,
          },
          inLanguage: 'pt-BR',
        },
        {
          '@type': 'ProfessionalService',
          '@id': `${canonicalUrl}/#service`,
          name: 'StaffPlus RH',
          image: ogImage,
          description: description,
          address: {
            '@type': 'PostalAddress',
            addressCountry: 'BR',
            addressRegion: 'SC',
          },
          geo: {
            '@type': 'GeoCoordinates',
          },
          url: canonicalUrl,
          telephone: '+55-47-9292-3530',
          priceRange: '$$',
          areaServed: {
            '@type': 'Country',
            name: 'Brasil',
          },
          serviceType: [
            'Gestão de Recursos Humanos',
            'Folha de Pagamento',
            'Consultoria Trabalhista',
            'Admissão e Desligamento',
            'Controle de Ponto',
            'Gestão de Férias',
            'eSocial',
          ],
        },
        {
          '@type': 'WebPage',
          '@id': `${canonicalUrl}/#webpage`,
          url: canonicalUrl,
          name: title,
          isPartOf: {
            '@id': `${canonicalUrl}/#website`,
          },
          about: {
            '@id': `${canonicalUrl}/#organization`,
          },
          description: description,
          inLanguage: 'pt-BR',
        },
      ],
    };

    scriptTag.textContent = JSON.stringify(structuredData);
  }, [title, description, keywords, canonicalUrl, ogImage, ogType, twitterCard]);

  return null;
};

export default SEO;
